import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField, Alert,
  Box, Typography, CircularProgress
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { rawApiClient } from '../../services/api';
import RecoveryCodesPanel from './RecoveryCodesPanel';

/**
 * Set up two-step sign-in with an authenticator app (security hardening batch 4,
 * 30 Sep 2026). When `required` is true (the person's role needs it and it is not set
 * up yet) the dialog cannot be dismissed: the server refuses everything else until it
 * is done.
 */
export default function MfaEnrollmentDialog({ open, required = false, onClose, onEnabled }) {
  const [step, setStep] = useState('loading'); // loading | scan | codes | error
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStep('loading');
    setError('');
    setCode('');
    setSaved(false);
    (async () => {
      try {
        const res = await rawApiClient.post('/auth/mfa/setup');
        if (cancelled) return;
        setSetup(res.data);
        setStep('scan');
      } catch (err) {
        if (cancelled) return;
        setError('Could not start setup. Please try again.');
        setStep('error');
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await rawApiClient.post('/auth/mfa/enable', { code: code.replace(/\s+/g, '') });
      setRecoveryCodes(res.data.recoveryCodes || []);
      setStep('codes');
    } catch (err) {
      setError(err?.response?.data?.errors?.[0]?.msg || 'That code is not correct.');
    } finally {
      setBusy(false);
    }
  };

  const finish = () => {
    if (onEnabled) onEnabled();
    onClose();
  };

  const manualKey = setup ? setup.secret.replace(/(.{4})/g, '$1 ').trim() : '';

  return (
    <Dialog open={open} onClose={required ? undefined : onClose} disableEscapeKeyDown={required} fullWidth maxWidth="sm" aria-labelledby="mfa-setup-title">
      <DialogTitle id="mfa-setup-title">Set up two-step sign-in</DialogTitle>
      <DialogContent>
        {required && step !== 'codes' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Your role needs two-step sign-in, to protect the clinical records you can see. It takes a minute.
          </Alert>
        )}
        {step === 'loading' && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
        {step === 'error' && <Alert severity="error">{error}</Alert>}
        {step === 'scan' && setup && (
          <Box component="form" onSubmit={confirm} id="mfa-setup-form">
            <DialogContentText sx={{ mb: 2 }}>
              1. Open an authenticator app on your phone (for example Google Authenticator, Microsoft Authenticator or 1Password) and scan this code.
            </DialogContentText>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, p: 2, bgcolor: '#fff' }}>
              <QRCodeSVG value={setup.otpauthUri} size={180} includeMargin />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Can't scan it? Enter this key in the app instead: <strong style={{ fontFamily: 'monospace' }}>{manualKey}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tip: you can scan the same code with a second app too, such as a password manager on your computer, so you can still sign in if your phone isn't to hand.
            </Typography>
            <DialogContentText sx={{ mb: 1 }}>2. Enter the 6-digit code the app shows.</DialogContentText>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              id="mfa-setup-code"
              autoFocus fullWidth label="6-digit code" inputMode="numeric" autoComplete="one-time-code"
              value={code} onChange={e => setCode(e.target.value)}
              inputProps={{ maxLength: 7 }}
            />
          </Box>
        )}
        {step === 'codes' && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>Two-step sign-in is on.</Alert>
            <RecoveryCodesPanel codes={recoveryCodes} saved={saved} onSavedChange={setSaved} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!required && step !== 'codes' && <Button onClick={onClose}>Cancel</Button>}
        {step === 'scan' && <Button type="submit" form="mfa-setup-form" variant="contained" disabled={busy || code.replace(/\s+/g, '').length !== 6}>{busy ? 'Checking…' : 'Turn on'}</Button>}
        {step === 'codes' && <Button variant="contained" onClick={finish} disabled={!saved}>Done</Button>}
      </DialogActions>
    </Dialog>
  );
}
